<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';

import ReportHeader from './components/ReportHeader.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';

import MediaTab from './instagram/MediaTab.vue';
import InsightsTab from './instagram/InsightsTab.vue';
import TrackerTab from './instagram/TrackerTab.vue';
import MonitorTab from './instagram/MonitorTab.vue';
import ListeningTab from './instagram/ListeningTab.vue';
import RepostsPendentesTab from './instagram/RepostsPendentesTab.vue';
import RadarRepercussaoTab from './instagram/RadarRepercussaoTab.vue';
import PostsAuditadosTab from './instagram/PostsAuditadosTab.vue';
import ConfigAdversariosTab from './instagram/ConfigAdversariosTab.vue';
import ConfigGeralTab from './instagram/ConfigGeralTab.vue';
import CreditosTab from './instagram/CreditosTab.vue';
import RelatorioMensalTab from './instagram/RelatorioMensalTab.vue';
import RankingLiderancasTab from './instagram/RankingLiderancasTab.vue';
import NotificacoesAgendadasTab from './instagram/NotificacoesAgendadasTab.vue';
import PainelPoliticoTab from './instagram/PainelPoliticoTab.vue';
import PublicacoesTab from './instagram/PublicacoesTab.vue';
import AssistenteIATab from './instagram/AssistenteIATab.vue';

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const selectedInbox = ref(null);
const groupBy = ref('day');

const currentDateRangeSelection = ref({
  id: 0,
  name: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS'),
});
const customDateRange = ref([new Date(), new Date()]);
const CUSTOM_DATE_RANGE_ID = 5;

const dateRange = computed(() => [
  { id: 0, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_7_DAYS') },
  { id: 1, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_30_DAYS') },
  { id: 2, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_3_MONTHS') },
  { id: 3, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_6_MONTHS') },
  { id: 4, name: t('REPORT.DATE_RANGE_OPTIONS.LAST_YEAR') },
  { id: 5, name: t('REPORT.DATE_RANGE_OPTIONS.CUSTOM_DATE_RANGE') },
]);

const groupByOptions = computed(() => [
  { id: 'day', name: t('REPORT.GROUPING_OPTIONS.DAY') },
  { id: 'week', name: t('REPORT.GROUPING_OPTIONS.WEEK') },
  { id: 'month', name: t('REPORT.GROUPING_OPTIONS.MONTH') },
]);

const isDateRangeSelected = computed(
  () => currentDateRangeSelection.value.id === CUSTOM_DATE_RANGE_ID
);

const instagramInboxes = computed(() => {
  const allInboxes = store.getters['inboxes/getInboxes'] || [];
  return allInboxes.filter(i => i.channel_type === 'Channel::Instagram');
});

const fromDate = computed(() => {
  if (isDateRangeSelected.value) {
    return getUnixTime(startOfDay(customDateRange.value[0]));
  }
  const dateRangeMap = { 0: 6, 1: 29, 2: 89, 3: 179, 4: 364 };
  const diff = dateRangeMap[currentDateRangeSelection.value.id];
  return getUnixTime(startOfDay(subDays(new Date(), diff)));
});

const toDate = computed(() => {
  if (isDateRangeSelected.value) {
    return getUnixTime(endOfDay(customDateRange.value[1]));
  }
  return getUnixTime(endOfDay(new Date()));
});

const validTabs = ['painel', 'publicacoes', 'media', 'insights', 'tracker', 'monitor', 'listening', 'reposts', 'radar', 'audited_posts', 'config_adversarios', 'config_geral', 'creditos', 'relatorio', 'ranking', 'assistente', 'notificacoes'];
const initialTab = validTabs.includes(route.query.tab) ? route.query.tab : 'painel';
const activeTab = ref(initialTab);

const TABS = [
  { id: 'painel', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_PAINEL_POLITICO' },
  { id: 'publicacoes', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_PUBLICACOES' },
  { id: 'media', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_MEDIA' },
  { id: 'insights', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_INSIGHTS' },
  { id: 'tracker', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_TRACKER' },
  { id: 'monitor', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_MONITOR' },
  { id: 'listening', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_LISTENING' },
  { id: 'reposts', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_REPOSTS' },
  { id: 'radar', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_RADAR' },
  { id: 'audited_posts', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_AUDITED_POSTS' },
  {
    id: 'config_adversarios',
    key: 'INSTAGRAM_MEDIA_REPORTS.TAB_CONFIG_ADVERSARIOS',
  },
  { id: 'config_geral', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_CONFIG_GERAL' },
  { id: 'creditos', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_CREDITOS' },
  { id: 'relatorio', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_RELATORIO' },
  { id: 'ranking', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_RANKING' },
  { id: 'assistente', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_ASSISTENTE_IA' },
  { id: 'notificacoes', key: 'INSTAGRAM_MEDIA_REPORTS.TAB_NOTIFICACOES' },
];

function onTabChange(tab) {
  activeTab.value = tab;
}

function onDateRangeChange(selectedRange) {
  currentDateRangeSelection.value = selectedRange;
}

function onCustomDateRangeChange(value) {
  customDateRange.value = value;
}

function onGroupByChange(option) {
  groupBy.value = option.id;
}

function onInboxChange(inbox) {
  selectedInbox.value = inbox;
}

watch(
  instagramInboxes,
  newVal => {
    if (newVal.length > 0 && !selectedInbox.value) {
      selectedInbox.value = newVal[0];
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col h-full overflow-y-auto px-4">
    <div class="flex flex-col gap-4">
    <ReportHeader
      :header-title="$t('INSTAGRAM_MEDIA_REPORTS.HEADER')"
      :header-content="$t('INSTAGRAM_MEDIA_REPORTS.DESCRIPTION')"
    />

    <!-- Filters Row -->
    <div class="flex flex-wrap items-center gap-3 mb-2">
      <!-- Inbox selector -->
      <div class="flex items-center gap-2">
        <label class="text-sm text-n-slate-11">
          {{ $t('INSTAGRAM_MEDIA_REPORTS.SELECT_INBOX') }}
        </label>
        <select
          class="text-sm rounded-lg border border-n-weak bg-n-solid-2 px-3 py-1.5 text-n-slate-12"
          @change="
            onInboxChange(
              instagramInboxes.find(i => i.id === Number($event.target.value))
            )
          "
        >
          <option
            v-for="inbox in instagramInboxes"
            :key="inbox.id"
            :value="inbox.id"
            :selected="selectedInbox?.id === inbox.id"
          >
            {{ inbox.name }}
          </option>
        </select>
      </div>

      <!-- Date range -->
      <div class="flex items-center gap-2">
        <select
          class="text-sm rounded-lg border border-n-weak bg-n-solid-2 px-3 py-1.5 text-n-slate-12"
          @change="
            onDateRangeChange(
              dateRange.find(d => d.id === Number($event.target.value))
            )
          "
        >
          <option
            v-for="option in dateRange"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </div>

      <!-- Custom date range picker -->
      <WootDateRangePicker
        v-if="isDateRangeSelected"
        :value="customDateRange"
        :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
        :placeholder="$t('REPORT.CUSTOM_DATE_RANGE.PLACEHOLDER')"
        @change="onCustomDateRangeChange"
      />

      <!-- Group by -->
      <div class="flex items-center gap-2">
        <select
          class="text-sm rounded-lg border border-n-weak bg-n-solid-2 px-3 py-1.5 text-n-slate-12"
          @change="
            onGroupByChange(
              groupByOptions.find(o => o.id === $event.target.value) ||
                groupByOptions[0]
            )
          "
        >
          <option
            v-for="option in groupByOptions"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex gap-1 border-b border-n-weak overflow-x-auto flex-shrink-0 sticky top-0 bg-n-background z-10">
      <button
        v-for="tab in TABS"
        :key="tab.id"
        class="px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap"
        :class="
          activeTab === tab.id
            ? 'text-n-blue-11 border-b-2 border-n-blue-9'
            : 'text-n-slate-11 hover:text-n-slate-12'
        "
        @click="onTabChange(tab.id)"
      >
        {{ $t(tab.key) }}
      </button>
    </div>

    <!-- Tab Content -->
    <PainelPoliticoTab
      v-if="activeTab === 'painel'"
      :selected-inbox="selectedInbox"
    />

    <PublicacoesTab
      v-if="activeTab === 'publicacoes'"
      :selected-inbox="selectedInbox"
      :from-date="fromDate"
      :to-date="toDate"
    />

    <MediaTab
      v-if="activeTab === 'media'"
      :selected-inbox="selectedInbox"
      :from-date="fromDate"
      :to-date="toDate"
      :group-by="groupBy"
    />

    <InsightsTab
      v-if="activeTab === 'insights'"
      :selected-inbox="selectedInbox"
      :from-date="fromDate"
      :to-date="toDate"
      :group-by="groupBy"
    />

    <TrackerTab
      v-if="activeTab === 'tracker'"
      :selected-inbox="selectedInbox"
    />

    <MonitorTab
      v-if="activeTab === 'monitor'"
      :selected-inbox="selectedInbox"
    />

    <ListeningTab
      v-if="activeTab === 'listening'"
      :selected-inbox="selectedInbox"
    />

    <RepostsPendentesTab
      v-if="activeTab === 'reposts'"
      :selected-inbox="selectedInbox"
    />

    <RadarRepercussaoTab
      v-if="activeTab === 'radar'"
      :selected-inbox="selectedInbox"
    />

    <PostsAuditadosTab
      v-if="activeTab === 'audited_posts'"
      :selected-inbox="selectedInbox"
    />

    <ConfigAdversariosTab
      v-if="activeTab === 'config_adversarios'"
      :selected-inbox="selectedInbox"
    />

    <ConfigGeralTab
      v-if="activeTab === 'config_geral'"
      :selected-inbox="selectedInbox"
    />

    <CreditosTab
      v-if="activeTab === 'creditos'"
      :selected-inbox="selectedInbox"
    />

    <RelatorioMensalTab
      v-if="activeTab === 'relatorio'"
      :selected-inbox="selectedInbox"
    />

    <RankingLiderancasTab
      v-if="activeTab === 'ranking'"
      :selected-inbox="selectedInbox"
    />

    <AssistenteIATab
      v-if="activeTab === 'assistente'"
      :selected-inbox="selectedInbox"
    />

    <NotificacoesAgendadasTab
      v-if="activeTab === 'notificacoes'"
      :selected-inbox="selectedInbox"
    />
    </div>
  </div>
</template>
